import React, { useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, A11y, Autoplay } from 'swiper/modules'
import AppLink from '../shared/AppLink'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

export default function PhotoSpotsGallery({
  spots = [],
  activeIndex = 0,
  onActiveIndexChange
}) {
  const swiperRef = useRef(null)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleSlideChange = (swiper) => {
    if (typeof onActiveIndexChange === 'function') {
      onActiveIndexChange(swiper.activeIndex || 0)
    }
  }

  useEffect(() => {
    if (!swiperRef.current) return
    if (typeof activeIndex !== 'number') return
    if (swiperRef.current.activeIndex === activeIndex) return
    swiperRef.current.slideTo(activeIndex)
  }, [activeIndex])

  if (!spots.length) {
    return <div className="text-white/70 text-center py-12">No photo spots match those filters yet.</div>
  }

  const activeSpot = spots[activeIndex] || spots[0]

  return (
    <div className="border border-white/10 overflow-hidden shadow-2xl bg-white/5 backdrop-blur-sm">
      <Swiper
        key={isMobile ? 'photo-gallery-mobile' : 'photo-gallery-desktop'}
        modules={[Navigation, Pagination, A11y, Autoplay]}
        slidesPerView={1}
        navigation
        pagination={
          isMobile
            ? { type: 'progressbar' }
            : { clickable: true }
        }
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        onSlideChange={handleSlideChange}
        onSwiper={(swiper) => {
          swiperRef.current = swiper
        }}
        initialSlide={Math.min(activeIndex || 0, spots.length - 1)}
        className="w-full photo-planner-gallery"
      >
        {spots.map((spot) => {
          const slugValue = typeof spot.slug === 'string' ? spot.slug : spot.slug?.current
          return (
            <SwiperSlide key={spot._id || slugValue || spot.name}>
              <div className="relative h-[200px] sm:h-[420px] lg:h-[480px]">
                {spot.images?.[0]?.asset?.url ? (
                  <img
                    src={spot.images[0].asset.url}
                    alt={spot.images?.[0]?.alt || spot.name}
                    className="absolute inset-0 w-full h-full object-contain"
                    loading="lazy"
                  />
                ) : null}
                <div className="absolute inset-x-0 bottom-0 p-4 pb-8 sm:p-5 sm:pb-9">
                  <h3 className="text-xl sm:text-2xl font-semibold text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                    {spot.name}
                  </h3>
                </div>
              </div>
            </SwiperSlide>
          )
        })}
      </Swiper>
      <div className="px-4 py-3 sm:px-6 sm:py-4 border-t border-white/10">
        {activeSpot?.shortDescription ? (
          <p className="text-xs sm:text-sm text-white/80">
            {activeSpot.shortDescription}{' '}
            {activeSpot?.slug ? (
              <AppLink
                to={`/photo-planner/${activeSpot.slug}`}
                className="text-emerald-300 hover:text-emerald-200 font-semibold"
              >
                How to get this shot →
              </AppLink>
            ) : null}
          </p>
        ) : activeSpot?.slug ? (
          <AppLink
            to={`/photo-planner/${activeSpot.slug}`}
            className="text-xs sm:text-sm text-emerald-300 hover:text-emerald-200 font-semibold"
          >
            How to get this shot →
          </AppLink>
        ) : null}
        {activeSpot?.images?.[0]?.attribution?.authorName ? (
          <div className="mt-2 text-[11px] sm:text-xs text-white/60">
            {(() => {
              const license = activeSpot.images?.[0]?.attribution?.license || ''
              const isArr = license.toLowerCase().includes('all rights reserved')
              return (
                <>
                  Photo by{' '}
                  {activeSpot.images[0].attribution.authorUrl || activeSpot.images[0].attribution.sourceUrl ? (
                    <a
                      href={activeSpot.images[0].attribution.authorUrl || activeSpot.images[0].attribution.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-emerald-200"
                    >
                      {activeSpot.images[0].attribution.authorName}
                    </a>
                  ) : (
                    activeSpot.images[0].attribution.authorName
                  )}
                  {isArr ? <>. Used with permission.</> : null}
                  {activeSpot.images?.[0]?.attribution?.license ? (
                    <>
                      {' '}
                      ·{' '}
                      {activeSpot.images[0].attribution.licenseUrl ? (
                        <a
                          href={activeSpot.images[0].attribution.licenseUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-emerald-200"
                        >
                          {activeSpot.images[0].attribution.license}
                        </a>
                      ) : (
                        activeSpot.images[0].attribution.license
                      )}
                    </>
                  ) : null}
                </>
              )
            })()}
          </div>
        ) : null}
      </div>
    </div>
  )
}
